import { Button, Checkbox, IconButton } from '@mui/joy';
import { useFormikContext } from 'formik';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type AutoListHandler } from '@/shared/hooks/useAutoAppendList';
import { type Parent } from './RelationDivisionLine/RelationDivisionLine.types';

import { trpc } from '@/config/trpc';

import { divideByRelationShares, getPercentageTotal } from './RelationDivisionLines.utils';
import { getShareRelationKey } from './RelationDivisionLine/RelationDivisionLine.utils';

import {
  useAddEmptyShare,
  useDivisionValues,
  useParent,
  useUnitRelationOptionsQueryParams,
} from './RelationDivisionLines.hooks';
import useAutoAppendList from '@/shared/hooks/useAutoAppendList';

import { AddIcon, InlineSpinner } from '@/shared/icons/Icons';
import FormikRelationDivisionLine from './RelationDivisionLine/FormikRelationDivisionLine';
import RelationDivisionLine from './RelationDivisionLine/RelationDivisionLine';
import RelationDivisionLinesProvider from './RelationDivisionLines.Provider';
import SelectDivisionKey from '@/shared/components/2.0/select/SelectDivisionKey';

type DivisionLinesPrefillInput = {
  bookYearId?: string | null;
  divisionKeyId: string;
  homeownerAssociationId: string;
};

const AddAction = () => {
  const { addEmptyShare, disabled } = useAddEmptyShare();

  return (
    <IconButton className="mr-2" disabled={disabled} onClick={addEmptyShare}>
      <AddIcon />
    </IconButton>
  );
};

const DivisionLinesOptions = () => {
  const { t } = useTranslation();

  const params = useUnitRelationOptionsQueryParams();
  const parent = useParent();

  return (
    <Checkbox
      checked={params.showOldRelations}
      className="mb-4"
      color="primary"
      disabled={parent.readOnly}
      label={t('showOldContacts')}
      onChange={() => params.setShowOldRelations(!params.showOldRelations)}
    />
  );
};

type DivisionLinesPrefillProps = {
  totalAmount: number;
};

const DivisionLinesPrefill: React.FC<DivisionLinesPrefillProps> = ({ totalAmount }) => {
  const [divisionKeyId, setDivisionKeyId] = useState<string | null>(null);

  const { t } = useTranslation();

  const formik = useFormikContext();
  const parent = useParent();
  const trpcUtils = trpc.useUtils();

  const createSharesFromDivisionKeyMutation = useMutation({
    mutationFn: async (input: DivisionLinesPrefillInput) => {
      const promises = {
        relationShares: trpcUtils.hoa.divisionKey.relationShares.fetch(input),
        unitRelationOptions: trpcUtils.hoa.building.relation.unitRelationsAutocomplete.fetch({
          bookYearId: input.bookYearId ?? undefined,
          homeownerAssociationId: input.homeownerAssociationId,
          options: { includeOldRelations: false },
        }),
      };

      const [relationShares, unitRelationOptions] = await Promise.all([
        promises.relationShares,
        promises.unitRelationOptions,
      ]);

      return divideByRelationShares({ relationShares, totalAmount, unitRelationOptions });
    },
  });

  const { bookYearId, homeownerAssociationId, name, readOnly } = parent;

  const applyDivisionKeyEnabled = !!homeownerAssociationId && !!divisionKeyId;

  const applyDivisionKey = async () => {
    if (!applyDivisionKeyEnabled) return;

    const shares = await createSharesFromDivisionKeyMutation.mutateAsync({
      bookYearId,
      divisionKeyId,
      homeownerAssociationId,
    });

    formik.setFieldValue(name, shares);
  };

  return (
    <SelectDivisionKey
      className="mb-4"
      disabled={readOnly}
      homeownerAssociationId={homeownerAssociationId}
      label={t('divisionKey')}
      value={divisionKeyId}
      onChange={(option) => setDivisionKeyId(option?.value ?? null)}
    >
      <Button
        disabled={
          readOnly || !applyDivisionKeyEnabled || createSharesFromDivisionKeyMutation.isLoading
        }
        startDecorator={createSharesFromDivisionKeyMutation.isLoading && <InlineSpinner />}
        onClick={applyDivisionKey}
      >
        {t('_bookings.actions.applyDivisionKey')}
      </Button>
    </SelectDivisionKey>
  );
};

type PlaceHolderLineProps = {
  linesHandler: AutoListHandler<HTMLLIElement>;
};

const PlaceholderRelationDivisionLine: React.FC<PlaceHolderLineProps> = ({ linesHandler }) => {
  const { addEmptyShare, disabled } = useAddEmptyShare();

  const values = useDivisionValues();

  if (disabled) return null;

  return (
    <RelationDivisionLine
      removeDisabled
      divisionType={values.divisionType}
      maxPercentage={100 - getPercentageTotal(values.divisions)}
      onFocus={linesHandler.createPlaceholderFocusHandler(addEmptyShare)}
    />
  );
};

type Props = React.PropsWithChildren<{
  additionalActions?: React.ReactNode;
  parent: Parent;
  totalAmount: number;
}>;

const RelationDivisionLines: React.FC<Props> = ({
  additionalActions,
  children,
  parent,
  totalAmount,
}) => {
  const { divisions } = useDivisionValues({
    divisionTypeName: parent.divisionTypeName,
    parentName: parent.name,
  });

  const linesHandler = useAutoAppendList<HTMLLIElement>({ list: divisions });

  return (
    <RelationDivisionLinesProvider divisions={divisions} parent={parent}>
      <DivisionLinesPrefill totalAmount={totalAmount} />
      <ul className="pl-0">
        {divisions.map((division, index) => (
          <FormikRelationDivisionLine
            key={getShareRelationKey(division) ?? index}
            ref={linesHandler.registerItem(index)}
            index={index}
            totalAmount={totalAmount}
          />
        ))}
        <PlaceholderRelationDivisionLine linesHandler={linesHandler} />
      </ul>
      <DivisionLinesOptions />
      {children}
      <div className="flex justify-end gap-2">
        <AddAction />
        {additionalActions}
      </div>
    </RelationDivisionLinesProvider>
  );
};

export default RelationDivisionLines;
